import { BUFFER_ROWS_ABOVE, easeOutCubic, easeOutQuad } from "./ReelSpinParams";
import type { ReelColumnContext, ReelLandStaggerCursor, ReelLane } from "./ReelSpinParams";
import type { SymbolView } from "@slot/presentation";

/** A reel column's animation phase (HOLD → … → DONE). */
export type Phase =
  | "HOLD"
  | "WINDUP"
  | "SPINUP"
  | "SCROLL"
  | "ANTICIPATE"
  | "DECEL"
  | "SETTLE"
  | "FINISH"
  | "DONE";

/**
 * ONE reel column: owns its drum lanes + motion state + the per-column phase machine.
 * The drum's thin per-frame driver calls applySlam() (on a slam request) then
 * tick(now, cursor) — this class runs the whole HOLD→…→DONE cascade, the SCROLL
 * commit-gate, the brake-start, and the landings. The only cross-column state (the
 * left→right land-stagger cursor) is passed in via `cursor` and read/mutated here.
 * Created by ReelDrum.buildDrum with its lanes; beginSpin() then binds the
 * per-spin context and (re)initialises the motion to rest at its staggered start.
 */
export class ReelColumn {
  // ── motion state: PRIVATE (this column's own tick/applySlam advance it). `off` is the
  // one field the driver reads — to carry an attached overlay out with the reel. ──
  private phase: Phase = "HOLD";
  public off = 0;
  private startAt = 0; // logical time this column leaves HOLD
  private anchorOff = 0; // SPINUP/SCROLL anchor (off = fn(anchorOff, now - anchorTime))
  private anchorTime = 0;
  private spinStartTime = 0; // downward motion begins (end of wind-up) — anchors baseMin + stop-stagger
  private decelStart = 0; // DECEL eased over [decelStart, decelEnd] to boundary+overshoot
  private decelEnd = 0;
  private decelFromOff = 0;
  private decelToOff = 0; // the row-aligned landing offset (final rest)
  private slamDecel = false; // DECEL easing: slam → easeOutCubic (snap); natural → easeOutQuad
  private committed = false; // natural: landing chosen + finals revealing (still SCROLL toward brake-start)
  private landRow = 0; // landing offset in rows (decelToOff/spacing) — drives the finals seeding
  private settleStart = 0;
  private settleEnd = 0;
  private seedFromOff = Number.POSITIVE_INFINITY; // +Inf until decel commits with KNOWN finals
  private cruiseV = 0; // current cruise speed: params.v normally, params.antV during/after ANTICIPATE
  private anticipateEnd = 0; // logical time the anticipation beat ends → commit the landing
  private anticipating = false; // true from ANTICIPATE entry until land (drives the crisp-filler choice)

  private ctx!: ReelColumnContext; // bound by beginSpin, before the tick runs

  public constructor(
    public readonly col: number,
    public readonly colX: number,
    public readonly lanes: ReelLane[],
  ) {}

  /** Bind the per-spin context + (re)initialise motion to rest at the staggered start. */
  public beginSpin(ctx: ReelColumnContext): void {
    this.ctx = ctx;
    const p = ctx.params;
    this.phase = "HOLD";
    this.off = 0;
    this.startAt = this.col * p.startStaggerSeconds;
    this.anchorOff = 0;
    this.anchorTime = 0;
    this.spinStartTime =
      this.col * p.startStaggerSeconds + (p.windUpPixels > 0 ? p.windUpSeconds : 0);
    this.decelStart = 0;
    this.decelEnd = 0;
    this.decelFromOff = 0;
    this.decelToOff = 0;
    this.slamDecel = false;
    this.committed = false;
    this.landRow = 0;
    this.settleStart = 0;
    this.settleEnd = 0;
    this.seedFromOff = Number.POSITIVE_INFINITY;
    this.cruiseV = p.v;
    this.anticipateEnd = 0;
    this.anticipating = false;
  }

  /** True once this column has fully landed (phase === "DONE"). */
  public get isDone(): boolean {
    return this.phase === "DONE";
  }

  /**
   * Advance this column ONE frame: the phase cascade (HOLD→WINDUP→SPINUP→SCROLL→DECEL→
   * SETTLE→FINISH→DONE), the SCROLL land-stagger commit-gate, the brake-start, and the
   * landings. `cursor` is the shared left→right land-stagger state (read + mutated). Fires
   * ctx.onColumnLanded the frame it lands.
   */
  public tick(now: number, cursor: ReelLandStaggerCursor): void {
    if (this.phase === "DONE") return;
    const params = this.ctx.params;
    const finalsKnown = this.ctx.finals() !== null;

    // Phase transitions.
    if (this.phase === "HOLD" && now >= this.startAt) {
      this.anchorOff = 0;
      this.anchorTime = this.startAt;
      this.phase =
        params.windUpPixels > 0 ? "WINDUP" : params.spinUpSeconds > 0 ? "SPINUP" : "SCROLL";
    }
    if (this.phase === "WINDUP" && now >= this.startAt + params.windUpSeconds) {
      // Release out of the pull-up: into the spin-up ramp if enabled, else
      // straight to full-speed scroll. anchorOff = top of the pull (-windUp).
      this.anchorOff = -params.windUpPixels;
      this.anchorTime = this.startAt + params.windUpSeconds;
      this.phase = params.spinUpSeconds > 0 ? "SPINUP" : "SCROLL";
    }
    if (this.phase === "SPINUP" && now >= this.anchorTime + params.spinUpSeconds) {
      // Accel reached full speed v → cruise. Re-anchor SCROLL at the spin-up
      // end offset/time so BOTH position and velocity are continuous (the
      // ramp ends at exactly v, the steady scroll's speed).
      this.anchorOff = this.anchorOff + 0.5 * params.v * params.spinUpSeconds;
      this.anchorTime = this.anchorTime + params.spinUpSeconds;
      this.phase = "SCROLL";
    }

    if (this.phase === "SCROLL") {
      const curOff = this.offAt(now);
      if (!this.committed) {
        // Land-stagger gate: a reel reaches its stop turn once (a) finals are known,
        // (b) strictly left→right (nextNaturalCol), (c) spun ≥ baseMin, and (d) the full
        // reveal+brake from NOW would land at/after the previous reel's landing +
        // stopStagger. Gating on the predicted LAND time (not the start) keeps strict
        // finishing order. (Turbo: stagger 0 + identical offsets → lockstep.)
        const gateOpen =
          finalsKnown &&
          this.col === cursor.nextNaturalCol &&
          now - this.spinStartTime >= params.baseMinSpinSeconds &&
          now + this.naturalDecelDur(curOff) >= cursor.naturalLandCursor;
        if (gateOpen && this.shouldAnticipate()) {
          // Server-flagged reel: slow + extend before stopping — but ONLY once the PREVIOUS reel
          // has actually LANDED (lastLandedCol >= col - 1), so the tease begins after the prior
          // reel hits, not while the cascade is still revealing. Until then keep scrolling at full
          // speed (uncommitted), holding nextNaturalCol so reels to the right also wait.
          if (cursor.lastLandedCol >= this.col - 1) {
            // The previous reel has landed. The FIRST anticipation of the spin holds an extra
            // start-delay past that landing (a held breath) before the beat; later flagged reels
            // chain immediately (anticipationStarted). The reel keeps scrolling at full speed
            // through the delay — still uncommitted, holding the cursor.
            const delayDone =
              cursor.anticipationStarted ||
              now >= cursor.lastLandedTime + params.anticipationStartDelaySeconds;
            if (delayDone) {
              cursor.anticipationStarted = true;
              // Enter ANTICIPATE (ramp v→antV, hold the beat) instead of committing, and HOLD
              // nextNaturalCol so reels to the right keep cruising + wait — this reel commits +
              // advances the cursor only at anticipateEnd.
              this.anchorOff = curOff;
              this.anchorTime = now;
              this.cruiseV = params.antV;
              this.anticipateEnd = now + params.anticipationDurationSeconds;
              this.anticipating = true;
              // Crisp the symbols ALREADY on screen now — applyOff only re-evaluates a lane's blur
              // when it next wraps, so without this the visible blurred fillers stay blurred until
              // they cycle around. Lanes wrapping IN during the beat are crisp via `anticipating`.
              if (params.anticipationCrisp) this.crispifyLanes();
              this.phase = "ANTICIPATE";
              this.ctx.onColumnAnticipateStart?.(this.col);
            }
          }
        } else if (gateOpen) {
          // Normal commit: pick the landing + start REVEALING the finals. The reel STAYS
          // in SCROLL — finals scroll in over ~one rotation — until the brake-start below.
          this.commitNaturalLanding(now, curOff, cursor);
        }
      } else if (curOff >= this.decelToOff - params.brakeDistance) {
        // Finals have revealed over the last ~rotation — now brake the last
        // brakeDistance px onto the landing. Velocity-matched easeOutQuad
        // (T = 2·D/v, D = remaining + overshoot) → enters at exactly the scroll
        // speed v, no lurch. DECEL dips to decelToOff + overshoot; SETTLE then
        // springs back up to decelToOff.
        this.decelFromOff = curOff;
        this.decelStart = now;
        this.decelEnd =
          now +
          Math.max(
            0.05,
            (2 * (this.decelToOff - curOff + params.overshoot)) / Math.max(1, this.cruiseV),
          );
        this.phase = "DECEL";
      }
    }
    if (this.phase === "ANTICIPATE" && now >= this.anticipateEnd) {
      // Beat over: commit the landing at the slow speed + return to (committed) SCROLL,
      // which reveals over the final slow rotation and brakes (cruiseV = antV). Advancing
      // the cursor here releases the held left→right sequence to the next reel.
      const aOff = this.offAt(now);
      this.anchorOff = aOff;
      this.anchorTime = now;
      this.phase = "SCROLL";
      this.commitNaturalLanding(now, aOff, cursor);
    }
    if (this.phase === "DECEL" && now >= this.decelEnd) {
      if (params.overshoot > 0) {
        // Reel is now at the overshoot low-point — hand off to the up-settle
        // (no stop on the rest line; the dip-down was the decel's own tail).
        // This is the impact moment (deepest dip, just before the spring
        // back up), so fire the land sound HERE, not when SETTLE ends.
        this.settleStart = this.decelEnd;
        this.settleEnd = this.decelEnd + params.settleSeconds;
        this.phase = "SETTLE";
        this.markLanded(now, cursor);
      } else {
        this.off = this.decelToOff;
        this.applyOff(this.off);
        this.finalize();
        this.phase = "DONE";
        this.markLanded(now, cursor);
        return;
      }
    }
    if (this.phase === "SETTLE" && now >= this.settleEnd) {
      // Land sound already fired at the overshoot low-point (DECEL→SETTLE).
      this.off = this.decelToOff;
      this.applyOff(this.off);
      this.finalize();
      this.phase = "DONE";
      return;
    }
    if (this.phase === "FINISH" && now >= this.decelEnd) {
      this.off = this.decelToOff;
      this.applyOff(this.off);
      this.finalize();
      this.phase = "DONE";
      this.markLanded(now, cursor);
      return;
    }

    this.off = this.offAt(now);
    this.applyOff(this.off);
  }

  /**
   * Slam response for THIS column (the driver captures the ONE shared end-time + calls
   * this on every not-DONE column). A column already in its settle bounce has landed (its
   * landing fired at the overshoot dip) and is left to finish the bounce on its own (finding 6,
   * 2026-09-14: a slam takes the reels still moving, never one that has landed). A committed
   * landing (DECEL, or a committed-but-still-revealing SCROLL) fast-finishes to its
   * already-chosen rest boundary; an uncommitted / held reel picks the nearest boundary via
   * commitSlam.
   */
  public applySlam(now: number, slamEnd: number): void {
    if (this.phase === "SETTLE") return;
    this.off = this.offAt(now);
    const committedLanding = this.phase === "DECEL" || (this.phase === "SCROLL" && this.committed);
    if (committedLanding) {
      // Already has a landing (decelToOff) + finals revealing — fast-finish
      // STRAIGHT to that rest boundary. Do NOT re-pick a boundary (ceil from
      // an off at/past the boundary, or the min-travel floor on a tiny
      // remaining distance, would add a whole rotation = the "slam during
      // the bounce → full spin" bug), and do NOT extend an almost-done
      // landing. DECEL caps at the sooner of natural/slam end; a
      // committed-but-still-revealing SCROLL reel has no brake yet → brake
      // over the slam window (its finals are already scrolling in, no pop).
      const naturalEnd = this.phase === "DECEL" ? this.decelEnd : slamEnd;
      this.decelFromOff = this.off;
      this.decelStart = now;
      this.decelEnd = Math.max(now, Math.min(naturalEnd, slamEnd));
      this.phase = "FINISH";
    } else {
      // Still scrolling (uncommitted) / held → pick the nearest boundary
      // (+ floor so a not-started reel still scrolls), land at the slam end.
      this.commitSlam(now, slamEnd);
    }
  }

  /** This reel is server-flagged to anticipate AND anticipation tuning is present. */
  private shouldAnticipate(): boolean {
    return (
      this.ctx.params.anticipationDurationSeconds > 0 &&
      (this.ctx.anticipates()?.includes(this.col) ?? false)
    );
  }

  /**
   * Commit the natural landing from offset `off`: choose the row-aligned target ~one
   * rotation + brake ahead, seed the reveal, and advance the land-stagger cursor. Shared by
   * the normal SCROLL gate and the ANTICIPATE→commit hand-off (each at its own cruiseV).
   */
  private commitNaturalLanding(now: number, off: number, cursor: ReelLandStaggerCursor): void {
    const params = this.ctx.params;
    this.committed = true;
    this.decelToOff = this.naturalDecelTarget(off); // ~1 rotation + brake ahead
    this.landRow = Math.round(this.decelToOff / params.spacing);
    this.seedFromOff = off; // finals start scrolling IN now (no pre-seed pop)
    this.slamDecel = false;
    cursor.naturalLandCursor = now + this.naturalDecelDur(off) + params.stopStaggerSeconds;
    cursor.nextNaturalCol++;
  }

  /**
   * Record this reel's landing (impact) on the shared cursor, then fire the land callback.
   * `lastLandedCol` gates a later flagged reel's ANTICIPATE entry (it waits for the reel before
   * it to LAND, not just commit). Reels land left→right, so this only ever moves forward.
   */
  private markLanded(now: number, cursor: ReelLandStaggerCursor): void {
    cursor.lastLandedCol = Math.max(cursor.lastLandedCol, this.col);
    cursor.lastLandedTime = now;
    this.ctx.onColumnLanded?.(this.col);
  }

  // ── analytic offset (PURE — no per-frame integration → no drift) ──
  private offAt(now: number): number {
    const params = this.ctx.params;
    switch (this.phase) {
      case "HOLD":
        return 0;
      case "WINDUP": {
        const p = Math.min(1, Math.max(0, (now - this.startAt) / params.windUpSeconds));
        return -params.windUpPixels * params.windUpEase(p);
      }
      case "SPINUP": {
        // Constant-acceleration ease-in from rest to full scroll speed v over
        // spinUpSeconds: off = anchorOff + ½·(v/T)·t². Velocity reaches exactly v at
        // t=T, so it flows into the constant-v SCROLL with no jump.
        const t = Math.min(params.spinUpSeconds, Math.max(0, now - this.anchorTime));
        return this.anchorOff + 0.5 * (params.v / params.spinUpSeconds) * t * t;
      }
      case "SCROLL":
        return this.anchorOff + this.cruiseV * (now - this.anchorTime);
      case "ANTICIPATE": {
        // Slow beat: ramp v→antV over anticipationRampInSeconds (quadratic, SPINUP in
        // reverse), then constant antV. Velocity-continuous at entry + ramp-end; rampIn=0
        // collapses to a hard drop. anchorOff/anchorTime are set at ANTICIPATE entry.
        const t = Math.max(0, now - this.anchorTime);
        const ramp = params.anticipationRampInSeconds;
        const v0 = params.v;
        const v1 = params.antV;
        if (t < ramp) {
          // velocity ramps v0→v1 linearly ⇒ off = anchorOff + v0·t + (v1−v0)·t²/(2·ramp)
          return this.anchorOff + v0 * t + ((v1 - v0) * t * t) / (2 * ramp);
        }
        const rampOff = this.anchorOff + ((v0 + v1) / 2) * ramp; // offset at the ramp end
        return rampOff + v1 * (t - ramp);
      }
      case "DECEL": {
        // Targets the overshoot low-point (boundary + overshoot). Natural stop uses
        // easeOutQuad (enters at exactly the scroll speed, no lurch); a SLAM keeps
        // easeOutCubic (the approved snap).
        const span = Math.max(1e-6, this.decelEnd - this.decelStart);
        const p = Math.min(1, Math.max(0, (now - this.decelStart) / span));
        const target = this.decelToOff + params.overshoot;
        const ease = this.slamDecel ? easeOutCubic : easeOutQuad;
        return this.decelFromOff + ease(p) * (target - this.decelFromOff);
      }
      case "SETTLE": {
        // Spring back UP from the overshoot low-point to the rest boundary.
        const span = Math.max(1e-6, this.settleEnd - this.settleStart);
        const q = Math.min(1, Math.max(0, (now - this.settleStart) / span));
        return this.decelToOff + params.overshoot * (1 - easeOutCubic(q));
      }
      case "FINISH": {
        // Slam fast-finish: straight to the already-chosen rest boundary, no overshoot.
        const span = Math.max(1e-6, this.decelEnd - this.decelStart);
        const p = Math.min(1, Math.max(0, (now - this.decelStart) / span));
        return this.decelFromOff + easeOutCubic(p) * (this.decelToOff - this.decelFromOff);
      }
      default:
        return this.decelToOff; // DONE
    }
  }

  // ── per-frame positioning + wrap-relabel of every lane at offset `absOff` ──
  private applyOff(absOff: number): void {
    if (this.ctx.aborted()) return;
    const params = this.ctx.params;
    const lanes = this.lanes;
    const x = this.colX;
    const inFinalRotation = absOff >= this.seedFromOff - 0.001;

    for (let k = 0; k < lanes.length; k++) {
      const lane = lanes[k];
      let y = lane.baseY + absOff;
      while (y > params.bandBottom) y -= params.drumHeight;
      if (y < lane.prevY - params.spacing * 0.5) {
        // Pre-commit scroll → random filler. During the reveal the keying depends on
        // the landing type: a SLAM lands on a rotation boundary, so its at-rest slot
        // map (restSymbolFor) is timing-independent (issue #2). A NATURAL stop lands
        // row-aligned, so it uses revealSymbolFor (rows-remaining) to keep the finals
        // contiguous and the buffer fillers out from between them (issue #1).
        const rest = !inFinalRotation
          ? { symbolId: this.ctx.pickSymbolId(), isFiller: true }
          : this.slamDecel
            ? this.restSymbolFor(k)
            : this.revealSymbolFor(y, absOff);
        // Fillers blur during the spin; an anticipating reel renders CRISP instead when the
        // tuning allows it (it scrolls slow enough to read). Finals are always crisp.
        const blurred = rest.isFiller && !(this.anticipating && params.anticipationCrisp);
        this.rebindLane(lane, rest.symbolId, blurred);
      }
      lane.view.node.position.set(x, y);
      // Hide a lane once it has scrolled ENTIRELY below the visible grid; re-shown the
      // frame it wraps back to the top.
      lane.view.node.visible = y < params.bandBottom - 0.5;
      lane.prevY = y;
    }
  }

  /**
   * Re-bind every lane to its CURRENT symbol CRISP — one-shot when the anticipation beat starts
   * (anticipationCrisp on). applyOff only re-evaluates a lane's blur when the lane WRAPS, so the
   * fillers already on screen would otherwise stay blurred until they scroll around; this swaps
   * them to crisp at once. At the gate the reel is pre-reveal, so every lane is a filler.
   */
  private crispifyLanes(): void {
    for (const lane of this.lanes) {
      const symbolId = lane.view.getSymbolId();
      if (symbolId) this.rebindLane(lane, symbolId, false); // false = crisp
    }
  }

  // ── force every lane onto the LANDED grid (the row-aligned final rest) ──
  private finalize(): void {
    const params = this.ctx.params;
    const lanes = this.lanes;
    const x = this.colX;
    for (let k = 0; k < lanes.length; k++) {
      const lane = lanes[k];
      // Position at the LANDED offset (decelToOff), NOT baseY — the reel can land
      // mid-rotation, so baseY would teleport the grid by the landing's row offset.
      let y = lane.baseY + this.decelToOff;
      while (y > params.bandBottom) y -= params.drumHeight;
      const rest = this.restSymbolFor(k);
      this.rebindLane(lane, rest.symbolId, rest.isFiller); // finals crisp; buffer fillers (off-screen) blurred
      lane.view.node.position.set(x, y);
      lane.view.node.visible = y < params.bandBottom - 0.5;
      lane.prevY = y;
    }
  }

  // ── natural-stop landing target + predicted duration (for the land-stagger gate) ──
  private naturalDecelTarget(off: number): number {
    const params = this.ctx.params;
    // ~one rotation + brakeDistance ahead, snapped UP to the row grid (ceil → the
    // reveal is always ≥ a full rotation, so every lane wraps+relabels to its final).
    return (
      Math.ceil((off + params.drumHeight + params.brakeDistance) / params.spacing) * params.spacing
    );
  }

  private naturalDecelDur(off: number): number {
    const params = this.ctx.params;
    const target = this.naturalDecelTarget(off);
    // reveal scroll (≈ one rotation at the cruise speed) + velocity-matched easeOutQuad
    // brake. cruiseV = v normally, antV for an anticipated reel.
    const revealTime = Math.max(0, target - params.brakeDistance - off) / Math.max(1, this.cruiseV);
    const brakeTime = (2 * (params.brakeDistance + params.overshoot)) / Math.max(1, this.cruiseV);
    return Math.max(0.05, revealTime + brakeTime);
  }

  // ── commit a SLAM stop: brake to the nearest FULL-rotation boundary (≥ slamMinTravel) ──
  private commitSlam(now: number, slamEnd: number): void {
    const params = this.ctx.params;
    let target = Math.ceil(this.off / params.drumHeight) * params.drumHeight;
    while (target - this.off < params.slamMinTravel) target += params.drumHeight; // whole rotations → stays on boundary
    this.decelFromOff = this.off;
    this.decelToOff = target;
    this.slamDecel = true;
    this.committed = true;
    this.landRow = Math.round(target / params.spacing);
    this.decelStart = now;
    this.decelEnd = slamEnd;
    // Pre-seed the at-rest finals onto EVERY lane, so the slam shows the real grid from its
    // first frame (the fast snap masks the swap). The drum applies a slam only once the finals
    // are known, so there is always a result to seed.
    this.seedFromOff = this.off;
    const lanes = this.lanes;
    for (let k = 0; k < lanes.length; k++) {
      const rest = this.restSymbolFor(k);
      this.rebindLane(lanes[k], rest.symbolId, rest.isFiller);
    }
    this.phase = "DECEL";
  }

  // ── internals ──────────────────────────────────────────────────────────────

  // LANDED symbol for lane k (finalize + slam pre-seed). At rest the lane occupies
  // visible slot ((k - BUFFER + landRow) mod laneCount): slots [0,gridRowCount) are
  // the visible grid (→ the actual finals), the rest are buffer lanes (→ random filler).
  private restSymbolFor(k: number): { symbolId: string; isFiller: boolean } {
    const params = this.ctx.params;
    const finals = this.ctx.finals()?.[this.col] ?? [];
    const slot =
      (((k - BUFFER_ROWS_ABOVE + this.landRow) % params.laneCount) + params.laneCount) %
      params.laneCount;
    if (slot < params.gridRowCount) {
      const sym = finals[slot];
      if (sym && sym.length > 0) return { symbolId: sym, isFiller: false };
    }
    return { symbolId: this.ctx.pickSymbolId(), isFiller: true };
  }

  /**
   * The landed (resting) symbol views for this reel, indexed by visible ROW
   * (0..gridRowCount-1, top→bottom). Reuses restSymbolFor's slot mapping: a lane
   * occupies visible slot ((k - BUFFER + landRow) mod laneCount); slots
   * [0,gridRowCount) are the visible grid. Buffer lanes are skipped (their row is
   * left null). Valid once the reel has finalized — call from the land hook.
   */
  public getLandedViewsInRowOrder(): (SymbolView | null)[] {
    const params = this.ctx.params;
    const result: (SymbolView | null)[] = new Array(params.gridRowCount).fill(null);
    for (let k = 0; k < this.lanes.length; k++) {
      const slot =
        (((k - BUFFER_ROWS_ABOVE + this.landRow) % params.laneCount) + params.laneCount) %
        params.laneCount;
      if (slot < params.gridRowCount) result[slot] = this.lanes[k].view;
    }
    return result;
  }

  // REVEAL symbol for a lane WRAPPING IN during the final approach (issues #1 + #2).
  // Keys on the row the lane will LAND in on THIS transit, as a SINGLE rounding of the
  // raw position `y` (avoids the turbo double-round flash). A lane shows a final only on
  // its final approach (landingRow in [0,gridRowCount)); earlier transits stay filler.
  private revealSymbolFor(y: number, absOff: number): { symbolId: string; isFiller: boolean } {
    const params = this.ctx.params;
    const rowsRemaining = (this.decelToOff - absOff) / params.spacing;
    const landingRow = Math.round((y - params.originY) / params.spacing + rowsRemaining);
    if (landingRow >= 0 && landingRow < params.gridRowCount) {
      const finals = this.ctx.finals()?.[this.col] ?? [];
      const sym = finals[landingRow];
      if (sym && sym.length > 0) return { symbolId: sym, isFiller: false };
    }
    return { symbolId: this.ctx.pickSymbolId(), isFiller: true };
  }

  // Re-bind a lane to a symbol, swapping its view first IF it's a dirty (pool-unsafe)
  // adopted win-overlay view: such a view can't be re-symboled cleanly, so once it has
  // scrolled far enough to recycle we destroy it (release destroys a non-reusable view,
  // taking the overlay with it) and acquire a fresh lane view. Clean lanes bind directly.
  private rebindLane(lane: ReelLane, symbolId: string, isFiller: boolean): void {
    if (!lane.view.isPoolReusable()) {
      try {
        this.ctx.release(lane.view); // non-reusable → destroyed
      } catch {
        // already released — safe to skip
      }
      lane.view = this.ctx.acquire();
    }
    this.ctx.applyLaneSymbol(lane.view, symbolId, isFiller);
  }
}
